// "Notes" — list, search, folder filter; onboarding when this device has no space yet.

import { Space } from '../space/space';
import { getSpace, requestPersistence, setSpace } from '../space/current';
import { titleOf, type NoteMeta } from '../notes/notes';
import { SECRET } from '../core/ids';
import { h, keep } from './dom';
import { canShareFiles, deviceName, saveFile, shareFiles } from './common';
import { BACKUP_EVERY_MS, exportStore, lastBackup, saveBackup } from '../notes/backup';
import { IdbStore } from '../space/store';
import type { View } from './view';

const COLOR_VAR: Record<string, string> = { red: 'var(--red)', amber: 'var(--amber)', green: 'var(--green)', blue: '#7a94a8', purple: '#9a8aa8', gray: '#808a86' };

export function colorOf(color: string | null): string {
  return (color && COLOR_VAR[color]) || 'transparent';
}

export function relativeDate(ms: number): string {
  const diff = Date.now() - ms;
  if (diff < 60_000) return 'à l’instant';
  if (diff < 3_600_000) return `il y a ${Math.floor(diff / 60_000)} min`;
  if (diff < 86_400_000) return `il y a ${Math.floor(diff / 3_600_000)} h`;
  return new Date(ms).toLocaleDateString('fr-FR', { day: 'numeric', month: 'short', year: diff > 300 * 86_400_000 ? 'numeric' : undefined });
}

/** "échéance 12 mars", amber when today or tomorrow, red when overdue (calendar days, UTC like notd). */
export function dueLabel(due: number | null): { text: string; cls: string } | null {
  if (due == null) return null;
  const today = Date.parse(`${new Date().toISOString().slice(0, 10)}T00:00:00Z`);
  const days = Math.round((due - today) / 86_400_000);
  const text = days === 0 ? 'aujourd’hui' : days === 1 ? 'demain' : days === -1 ? 'hier' : new Date(due).toLocaleDateString('fr-FR', { day: 'numeric', month: 'short', timeZone: 'UTC' });
  return { text: `échéance ${text}`, cls: days < 0 ? 'bad' : days <= 1 ? 'warn' : 'dim' };
}

export function isIosSafariTab(): boolean {
  const ios = /iPhone|iPad|iPod/.test(navigator.userAgent) || (navigator.platform === 'MacIntel' && navigator.maxTouchPoints > 1);
  const standalone = matchMedia('(display-mode: standalone)').matches || (navigator as unknown as { standalone?: boolean }).standalone === true;
  return ios && !standalone;
}

export function notesView(root: HTMLElement): View {
  let space: Space | null = null;
  let query = '';
  let folder: string | 'all' | 'archived' = 'all';
  let leftovers = 0;
  let backupAt: number | null = null;
  let backupMsg = '';
  let frame = 0;
  const schedule = () => {
    cancelAnimationFrame(frame);
    frame = requestAnimationFrame(render);
  };

  async function backupNow() {
    if (!space) return;
    const n = await saveBackup(space.store, saveFile, shareFiles, canShareFiles);
    backupAt = Date.now();
    backupMsg = `Sauvegarde de ${n} note${n > 1 ? 's' : ''} prête : range-la dans Fichiers, iCloud Drive ou un autre endroit sûr.`;
    render();
  }

  async function exportLeftovers() {
    const { zip, count } = await exportStore(new IdbStore());
    saveFile(new File([zip as Uint8Array<ArrayBuffer>], `notes-recuperees-${new Date().toISOString().slice(0, 10)}.zip`, { type: 'application/zip' }));
    backupMsg = `${count} note${count > 1 ? 's' : ''} exportée${count > 1 ? 's' : ''}. Tu pourras les réimporter (Appareils → Importer).`;
    render();
  }

  async function eraseLeftovers() {
    if (prompt('Ces notes seront définitivement effacées de cet appareil. Tape EFFACER pour confirmer.')?.trim().toUpperCase() !== 'EFFACER') return;
    await new IdbStore().clear();
    leftovers = 0;
    render();
  }

  async function create() {
    const s = await Space.create(deviceName());
    requestPersistence();
    setSpace(s);
    space = s;
    attach();
    render();
  }

  function attach() {
    if (!space) return;
    space.notes.addEventListener('change', schedule);
    space.notes.addEventListener('folders', schedule);
    space.sync?.addEventListener('peers', schedule);
    space.journal.addEventListener('envelope', schedule); // membership changes (revoked, added)
  }

  async function newNote() {
    if (!space) return;
    const id = await space.notes.create({ folder: folder !== 'all' && folder !== 'archived' ? folder : null });
    location.hash = `#/notes/${id}`;
  }

  function matches(n: NoteMeta): boolean {
    if (folder !== 'all' && folder !== 'archived' && n.folder !== folder) return false;
    if (!query) return true;
    const q = query.toLowerCase();
    return n.title.toLowerCase().includes(q) || n.excerpt.toLowerCase().includes(q);
  }

  function leftoversCard(): Node {
    return h(
      'section',
      { class: 'card' },
      h('h2', { text: 'Des notes d’un ancien espace sont encore là' }),
      h('p', { class: 'dim', text: `Cet appareil contient ${leftovers} note${leftovers > 1 ? 's' : ''} d’un espace dont il n’a plus les clés. Exporte-les avant de créer ou de rejoindre un espace : tu pourras les réimporter ensuite.` }),
      h('div', { class: 'row' }, h('button', { class: 'btn primary small', text: 'Exporter ces notes (.zip)', onclick: () => void exportLeftovers() }), h('button', { class: 'btn danger small', text: 'Les effacer', onclick: () => void eraseLeftovers() })),
    );
  }

  function onboarding(): (Node | string)[] {
    if (leftovers) return keep(h('h1', { text: 'Notes' }), backupMsg && h('p', { class: 'ok', text: backupMsg }), leftoversCard());
    // A home-screen app has no address bar: the link scanned by the camera opens in Safari, so it
    // can be pasted here instead (scan → long press → Copy).
    const pasted = h('input', { class: 'field', placeholder: 'colle ici le lien de liaison', autocomplete: 'off', 'aria-label': 'Lien de liaison' });
    const joinForm = h(
      'form',
      {
        class: 'stack',
        onsubmit: (e: Event) => {
          e.preventDefault();
          const secret = /(?:#\/link\/)?([A-Za-z0-9_-]{22})\s*$/.exec(pasted.value.trim())?.[1];
          if (secret && SECRET.test(secret)) location.hash = `#/link/${secret}`;
          else pasted.setCustomValidity('Lien de liaison non reconnu'), pasted.reportValidity();
        },
      },
      pasted,
      h('button', { class: 'btn', type: 'submit', text: 'Rejoindre avec ce lien' }),
    );
    pasted.addEventListener('input', () => pasted.setCustomValidity(''));
    return keep(
      h('h1', { text: 'Notes' }),
      h('p', { class: 'dim', text: 'Tes notes, uniquement sur tes appareils : synchronisées directement entre eux, chiffrées, sans serveur qui les garde.' }),
      h('button', { class: 'btn primary', text: 'Créer mon espace sur cet appareil', onclick: () => void create() }),
      h(
        'section',
        { class: 'card' },
        h('h2', { text: 'Déjà un espace sur un autre appareil ?' }),
        h('p', { class: 'dim', text: 'Sur cet autre appareil : Notes → Appareils → « Lier un appareil », puis scanne le QR code avec celui-ci (ou ouvre le lien affiché).' }),
        h('p', { class: 'dim small', text: 'App installée sur l’écran d’accueil : scanne avec l’appareil photo, appui long sur le lien → Copier, puis colle-le ici.' }),
        joinForm,
      ),
    );
  }

  function noteRow(n: NoteMeta): HTMLElement {
    const first = n.excerpt.split('\n').find((l) => l.trim() && l.trim() !== n.title.trim()) ?? '';
    const due = n.archived ? null : dueLabel(n.due);
    const item = h(
      'a',
      { class: 'note-row', href: `#/notes/${n.id}` },
      h('span', { class: 'note-color' }),
      h(
        'span',
        { class: 'note-main' },
        h('span', { class: 'note-title', text: `${n.pinned ? '📌 ' : ''}${titleOf(n)}` }),
        first && h('span', { class: 'note-excerpt dim', text: first.replace(/^#+\s*/, '') }),
        due && h('span', { class: `note-due ${due.cls}`, text: due.text }),
      ),
      h('span', { class: 'note-date dim', text: relativeDate(n.edited) }),
    );
    (item.firstChild as HTMLElement).style.background = colorOf(n.color);
    return item;
  }

  function list(s: Space): (Node | string)[] {
    const notes = s.notes.list({ archived: folder === 'archived' }).filter(matches);
    const folders = s.notes.activeFolders();
    const members = s.journal.activeMembers().length;
    const online = s.sync?.peerCount ?? 0;
    const chip = (id: typeof folder, label: string) =>
      h('button', { class: `chip${folder === id ? ' active' : ''}`, text: label, onclick: () => ((folder = id), render()) });
    const search = h('input', {
      class: 'field',
      type: 'search',
      placeholder: 'Rechercher…',
      value: query,
      oninput: (e: Event) => {
        query = (e.target as HTMLInputElement).value;
        schedule();
      },
    });
    return keep(
      h(
        'div',
        { class: 'row between' },
        h('h1', { text: 'Notes' }),
        h('a', { class: 'btn small', href: '#/devices', text: online ? `Appareils · ${online} en ligne` : 'Appareils' }),
      ),
      s.journal.isRevoked && h('p', { class: 'bad', text: 'Cet appareil a été retiré de ton espace : tes notes restent lisibles ici mais ne se synchronisent plus. Pour le réutiliser : Appareils → Quitter l’espace, puis lie-le à nouveau.' }),
      isIosSafariTab() && h('p', { class: 'warn small', text: 'Sur iPhone, ajoute l’app à l’écran d’accueil (Partager → Sur l’écran d’accueil) : sinon Safari peut effacer tes notes au bout de quelques jours.' }),
      members < 2 && h('p', { class: 'dim small' }, 'Un seul appareil : si tu le perds, tes notes sont perdues. ', h('a', { href: '#/devices', text: 'Lier un 2e appareil' }), '.'),
      backupMsg
        ? h('p', { class: 'ok small', text: backupMsg })
        : s.notes.index.size > 0 && (backupAt === null || Date.now() - backupAt > BACKUP_EVERY_MS) &&
          h(
            'div',
            { class: 'row backup-banner' },
            h('span', { class: 'warn small', text: backupAt ? `Dernière sauvegarde il y a ${Math.floor((Date.now() - backupAt) / 86_400_000)} jours.` : 'Aucune sauvegarde : si tous tes appareils perdent leurs données, tes notes sont perdues.' }),
            h('button', { class: 'btn small', text: 'Sauvegarder maintenant', onclick: () => void backupNow() }),
          ),
      h('button', { class: 'btn primary', text: '+ Nouvelle note', onclick: () => void newNote() }),
      search,
      h('div', { class: 'chips' }, chip('all', 'Toutes'), ...folders.map((f) => chip(f.id, f.name)), chip('archived', 'Archives')),
      notes.length === 0
        ? h('p', { class: 'dim', text: query ? 'Aucune note ne correspond.' : folder === 'archived' ? 'Aucune note archivée.' : 'Aucune note pour l’instant.' })
        : h('nav', { class: 'notes' }, ...notes.map(noteRow)),
    );
  }

  function render() {
    // Keep the search field focused while its results re-render.
    const focused = document.activeElement instanceof HTMLInputElement && document.activeElement.type === 'search';
    root.replaceChildren(...(space ? list(space) : onboarding()));
    if (focused) {
      const input = root.querySelector<HTMLInputElement>('input[type=search]');
      input?.focus();
      input?.setSelectionRange(query.length, query.length);
    }
  }

  root.replaceChildren(h('p', { class: 'dim', text: 'Ouverture…' }));
  void getSpace().then(async (s) => {
    space = s;
    if (s) backupAt = await lastBackup(s.store);
    else leftovers = await Space.leftovers();
    attach();
    render();
  });

  return {
    destroy() {
      cancelAnimationFrame(frame);
      space?.notes.removeEventListener('change', schedule);
      space?.notes.removeEventListener('folders', schedule);
      space?.sync?.removeEventListener('peers', schedule);
      space?.journal.removeEventListener('envelope', schedule);
    },
  };
}
